/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CommercialAgreement,
  CommercialAgreementPreview,
  CommercialLead,
  FiscalAddressFields,
  LeadInformation,
  LeadInformationFields,
  OrderInformation,
  OrderShipping,
  ReceptionContactFields,
  ShippingItemTime,
  ShippingPaginationResult,
  StreetTypes,
} from '@interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { LeadService, OrderService } from '@services';
import { convertDate } from '@utils';
import { DatePipe } from '@angular/common';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { Time } from '@constants';

@Component({
  selector: 'unicon-commercial-agreement-dialog',
  templateUrl: './commercial-agreement-dialog.component.html',
  styleUrl: './commercial-agreement-dialog.component.scss',
})
export class CommercialAgreementDialogComponent {
  protected showCommercialAgreement: boolean = true;
  protected dataLead?: CommercialAgreementPreview;
  protected lead: any;
  public form!: FormGroup;
  protected receptionContact: boolean = true;
  protected fiscalAddress: boolean = true;
  protected disabled: boolean = true;
  protected productList: any = [];
  protected hour: string = '';
  protected showCalendarGridModal: boolean = false;
  protected errorCalendar?: string;
  private resultDatesCalendar?: ShippingItemTime[];
  protected calendarGridResult!: ShippingPaginationResult;
  protected calendarOrderId?: string;
  protected dateCalendarGrid?: string;
  protected editForm: boolean = false;
  protected streetTypes: StreetTypes[] = [];
  protected selectedDate: string = '';
  protected startDateTime: string = '';
  protected endDateTime: string = '';
  protected isLoadingSaveEdit: boolean = false;
  protected isLoadingCreateAgreement: boolean = false;
  protected disabledCalendar$ = new BehaviorSubject<boolean>(false);
  private options: Intl.DateTimeFormatOptions = { weekday: 'long' };
  private formatDay: Intl.DateTimeFormat = new Intl.DateTimeFormat(
    'es-ES',
    this.options,
  );
  protected showMessageError: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CommercialLead,
    private formBuilder: FormBuilder,
    private orderService: OrderService,
    private leadService: LeadService,
    public dialogRefCommercial: MatDialogRef<CommercialAgreementDialogComponent>,
    private datePipe: DatePipe,
  ) {
    this.lead = data.lead;
  }

  ngOnInit(): void {
    this.getStreetTypes();
    this.getCommercialAgreementPreview(this.lead.id);
  }

  protected getStreetTypes() {
    this.leadService.streetTypes().subscribe({
      next: (types: StreetTypes[]) => {
        this.streetTypes = types;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
      },
    });
  }

  protected getCommercialAgreementPreview(orderId: string) {
    this.showCommercialAgreement = false;
    this.orderService.getCommercialAgreementPreview(orderId).subscribe({
      next: (order: CommercialAgreementPreview) => {
        this.showCommercialAgreement = true;
        this.dataLead = order;
        this.setForms(this.dataLead);
      },
      error: (error: HttpErrorResponse) => {
        this.showCalendarGridModal = false;
        this.errorCalendar = error.error.detail;
        setTimeout(() => {
          this.errorCalendar = undefined;
        }, Time.FIVE_SECONDS);
      },
    });
  }

  private transformTime(time: any) {
    const startTime = new Date(time.startDateTime);
    const endTime = new Date(time.endDateTime);
    return startTime && endTime
      ? `${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : '';
  }

  protected getFormattedDate(inputDate: string): string {
    const [day, month, year] = inputDate.split('-').map(Number);
    const originalDate = new Date(year, month - 1, day);

    originalDate.setDate(originalDate.getDate() - 1);

    const dias = [
      'Domingo',
      'Lunes',
      'Martes',
      'Miércoles',
      'Jueves',
      'Viernes',
      'Sábado',
    ];
    const diaSemana = dias[originalDate.getDay()];

    const dia = originalDate.getDate();
    const mes = originalDate.getMonth() + 1;

    return `${diaSemana}, ${dia}/${mes}/${originalDate.getFullYear()}`;
  }

  private isDepartmentDisabled(dataLead: CommercialAgreementPreview): boolean {
    return !!(dataLead.leadAddress && dataLead.leadAddress.department
      ? dataLead.leadAddress.department
      : '');
  }

  private getDepartment(dataLead: CommercialAgreementPreview): string {
    return dataLead.leadAddress && dataLead.leadAddress.department
      ? dataLead.leadAddress.department
      : '';
  }

  private isProvinceDisabled(dataLead: CommercialAgreementPreview): boolean {
    return !!(dataLead.leadAddress && dataLead.leadAddress.province
      ? dataLead.leadAddress.province
      : '');
  }

  private getProvince(dataLead: CommercialAgreementPreview): string {
    return dataLead.leadAddress && dataLead.leadAddress.province
      ? dataLead.leadAddress.province
      : '';
  }

  private isDistrictDisabled(dataLead: CommercialAgreementPreview): boolean {
    return !!(dataLead.leadAddress && dataLead.leadAddress.district
      ? dataLead.leadAddress.district
      : '');
  }

  private getDistrict(dataLead: CommercialAgreementPreview): string {
    return dataLead.leadAddress && dataLead.leadAddress.district
      ? dataLead.leadAddress.district
      : '';
  }

  private isStreetDisabled(dataLead: CommercialAgreementPreview): boolean {
    return !!(dataLead.leadAddress && dataLead.leadAddress.streetTypeId
      ? dataLead.leadAddress.streetTypeId
      : 0);
  }

  private getStreet(dataLead: CommercialAgreementPreview): number {
    return dataLead.leadAddress && dataLead.leadAddress.streetTypeId
      ? dataLead.leadAddress.streetTypeId
      : 0;
  }

  private isFiscalAddressDisabled(
    dataLead: CommercialAgreementPreview,
  ): boolean {
    return !!(dataLead.leadAddress && dataLead.leadAddress.addressName
      ? dataLead.leadAddress.addressName
      : '');
  }

  private getFiscalAddress(dataLead: CommercialAgreementPreview): string {
    return dataLead.leadAddress && dataLead.leadAddress.addressName
      ? dataLead.leadAddress.addressName
      : '';
  }

  private isContactInformationDisabled(
    dataLead: CommercialAgreementPreview,
  ): boolean {
    return !!(dataLead.contactInformation &&
    dataLead.contactInformation.completeName
      ? dataLead.contactInformation.completeName
      : '');
  }

  private getContactInformation(dataLead: CommercialAgreementPreview): string {
    return dataLead.contactInformation &&
      dataLead.contactInformation.completeName
      ? dataLead.contactInformation.completeName
      : '';
  }

  private isPhoneNumberDisabled(dataLead: CommercialAgreementPreview): boolean {
    return !!(dataLead.contactInformation && dataLead.contactInformation.phone
      ? dataLead.contactInformation.phone
      : '');
  }

  private getPhoneNumber(dataLead: CommercialAgreementPreview): string {
    return dataLead.contactInformation && dataLead.contactInformation.phone
      ? dataLead.contactInformation.phone
      : '';
  }

  protected validateFields(fields: string[]) {
    return fields.every(field => {
      const value = this.form.get(field)?.value;
      return value !== null && value !== '';
    });
  }

  private markFieldsAsTouched(): void {
    Object.keys(this.form.controls).forEach(field => {
      const control = this.form.get(field);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });
  }

  protected setForms(dataLead: CommercialAgreementPreview) {
    if (dataLead) {
      this.receptionContact =
        dataLead.contactInformation !== null ? false : this.receptionContact;
      this.fiscalAddress =
        dataLead.leadAddress !== null ? false : this.fiscalAddress;
      this.productList = dataLead.items;
      this.form = this.formBuilder.group({
        leadName: new FormControl(
          {
            disabled: !!dataLead.leadNames,
            value: dataLead.leadNames ?? '',
          },
          Validators.compose([Validators.required]),
        ),
        paternalSurname: new FormControl(
          {
            disabled: !!dataLead.leadFirstSurname,
            value: dataLead.leadFirstSurname ?? '',
          },
          Validators.compose([Validators.required]),
        ),
        maternalSurname: new FormControl(
          {
            disabled: !!dataLead.leadSecondSurname,
            value: dataLead.leadSecondSurname ?? '',
          },
          Validators.compose([Validators.required]),
        ),
        document: new FormControl(
          {
            disabled: !!dataLead.leadIdentificationDocumentNumber,
            value: dataLead.leadIdentificationDocumentNumber ?? '',
          },
          Validators.compose([Validators.required]),
        ),
        email: new FormControl(
          {
            disabled: !!dataLead.leadEmail,
            value: dataLead.leadEmail ?? '',
          },
          Validators.compose([Validators.required, Validators.email]),
        ),
        receptionContactName: new FormControl({
          disabled: this.isContactInformationDisabled(dataLead),
          value: this.getContactInformation(dataLead),
        }),
        phoneNumber: new FormControl({
          disabled: this.isPhoneNumberDisabled(dataLead),
          value: this.getPhoneNumber(dataLead),
        }),
        address: new FormControl(
          {
            disabled: !!dataLead.orderShipping.addressName,
            value: dataLead.orderShipping.addressName ?? '',
          },
          Validators.compose([Validators.required]),
        ),
        longitude: new FormControl({
          disabled: this.isDepartmentDisabled(dataLead),
          value: this.getDepartment(dataLead),
        }),
        latitude: new FormControl({
          disabled: this.isDepartmentDisabled(dataLead),
          value: this.getDepartment(dataLead),
        }),
        department: new FormControl({
          disabled: this.isDepartmentDisabled(dataLead),
          value: this.getDepartment(dataLead),
        }),
        province: new FormControl({
          disabled: this.isProvinceDisabled(dataLead),
          value: this.getProvince(dataLead),
        }),
        district: new FormControl({
          disabled: this.isDistrictDisabled(dataLead),
          value: this.getDistrict(dataLead),
        }),
        streetType: new FormControl({
          disabled: this.isStreetDisabled(dataLead),
          value: this.getStreet(dataLead),
        }),
        fiscalAddressName: new FormControl({
          disabled: this.isFiscalAddressDisabled(dataLead),
          value: this.getFiscalAddress(dataLead),
        }),
        total: new FormControl({
          disabled: true,
          value: dataLead.totalAmount,
        }),
      });
      this.markFieldsAsTouched();
    }
  }

  protected closeModal(): void {
    this.dialogRefCommercial.close();
  }

  protected onCreateAgreement() {
    this.isLoadingCreateAgreement = true;
    const request: CommercialAgreement = {
      leadEmail: this.receptionContact
        ? null
        : this.form.controls['email'].value,
      leadDepartment: this.fiscalAddress
        ? null
        : this.form.controls['department'].value,
      leadProvince: this.fiscalAddress
        ? null
        : this.form.controls['province'].value,
      leadDistrict: this.fiscalAddress
        ? null
        : this.form.controls['district'].value,
      leadStreetTypeId: this.fiscalAddress
        ? null
        : this.form.controls['streetType'].value,
      leadAddressName: this.fiscalAddress
        ? null
        : this.form.controls['fiscalAddressName'].value,
      shippingContactName: this.receptionContact
        ? null
        : this.form.controls['receptionContactName'].value,
      shippingContactPhone: this.receptionContact
        ? null
        : this.form.controls['phoneNumber'].value,
    };
    this.orderService
      .createCommercialAgreement(this.lead.id, request)
      .subscribe({
        next: () => {
          this.isLoadingCreateAgreement = false;
          this.dialogRefCommercial.close('success');
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
          this.isLoadingCreateAgreement = false;
        },
      });
  }

  protected onShippingResult(orderId: string, request: OrderShipping): void {
    this.orderService.updateOrderShipping(orderId, request).subscribe({
      error: (error: HttpErrorResponse) => {
        this.showCalendarGridModal = false;
        this.errorCalendar = error.error.detail;
        setTimeout(() => {
          this.errorCalendar = undefined;
        }, Time.FIVE_SECONDS);
      },
    });
  }

  private getOrderAvailability(
    orderId: string,
    date: string,
    page: number = 1,
    pageNumber: number = 6,
  ): void {
    this.orderService
      .shippingAvailability(orderId, convertDate(date), page, pageNumber)
      .subscribe({
        next: (data: ShippingPaginationResult) => {
          this.openCalendarGrid(orderId, date, data);
        },
        error: (error: HttpErrorResponse) => {
          this.showCalendarGridModal = false;
          this.errorCalendar = error.error.detail;
          setTimeout(() => {
            this.errorCalendar = undefined;
          }, Time.FIVE_SECONDS);
        },
      });
  }

  protected openCalendarGrid(
    calendarOrderId: string,
    date: string,
    data: ShippingPaginationResult,
  ): void {
    this.resultDatesCalendar = (data?.results || []).map(obj => {
      const newArray = obj?.availability.map(item => {
        const dateObj = new Date(item.startDateTime);
        const dayName = this.formatDay.format(dateObj);
        return { ...item, dayName };
      });
      return { ...obj, availability: newArray };
    });
    this.calendarGridResult = { ...data, results: this.resultDatesCalendar };
    this.dateCalendarGrid = date;
    this.calendarOrderId = calendarOrderId;
  }

  protected onSetCalendarGrid(
    { date }: { date: string },
    calendarOrderId: string,
  ): void {
    this.showCalendarGridModal = true;

    if (this.selectedDate === '') {
      this.selectedDate = date;
    }

    const regex = /^[A-Za-zñáéíóúÑÁÉÍÓÚ]+,\s\d{1,2}\/\d{1,2}\/\d{4}$/;
    if (!regex.test(date)) {
      const datePartIndex = date.indexOf('-') - 1;
      const dateSubString = date.substring(0, datePartIndex);
      this.getOrderAvailability(calendarOrderId, dateSubString);
    } else {
      this.getOrderAvailability(calendarOrderId, date);
    }
  }

  protected onCloseCalendarGrid(date: any): void {
    this.showCalendarGridModal = !this.showCalendarGridModal;
    this.dateCalendarGrid = undefined;
    this.calendarOrderId = undefined;
    this.startDateTime = date.startDateTime;
    this.endDateTime = date.endDateTime;
    if (this.dataLead) {
      if (date) {
        const newDateSelected = this.datePipe.transform(
          date.startDateTime,
          'dd-MM-yyyy',
        );
        this.selectedDate = this.getFormattedDate(newDateSelected ?? '');
        const newHour = this.transformTime(date);
        this.form.controls['hour'].setValue(newHour);
      }
    }
  }

  protected onEditCommercialAgreement() {
    this.disabled = false;
    this.editForm = true;
    this.disabledCalendar$.next(false);
    this.form.get('hour')?.enable();
    this.form.get('leadName')?.enable();
    this.form.get('paternalSurname')?.enable();
    this.form.get('maternalSurname')?.enable();
    this.form.get('document')?.enable();
    this.form.get('receptionContactName')?.enable();
    this.form.get('phoneNumber')?.enable();
    this.form.get('email')?.enable();
    this.form.get('address')?.enable();
    this.form.get('department')?.enable();
    this.form.get('province')?.enable();
    this.form.get('district')?.enable();
    this.form.get('streetType')?.enable();
    this.form.get('fiscalAddressName')?.enable();
  }

  protected onToggleFiscalAddress(checked: boolean): void {
    this.fiscalAddress = checked;
  }

  protected onTogglReceptionContact(checked: boolean): void {
    this.receptionContact = checked;
  }

  protected onSelectStreetTypeBox(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target?.value;
    if (value) {
      this.form.controls['streetType'].setValue(value);
    }
  }

  private updateLeadContactInformation(
    request: OrderShipping,
  ): Observable<any> {
    return this.orderService.updateOrderShipping(this.lead.id, request);
  }

  private updateLeadInformation(request: LeadInformation): Observable<any> {
    return this.leadService.updateLeadInformation(this.lead.leadId, request);
  }

  protected onSaveEdit() {
    const serviceEvents: Observable<any>[] = [];
    this.isLoadingSaveEdit = true;

    const leadInformation: LeadInformationFields = {
      email: this.form.controls['email'].value,
      identificationDocumentNumber: this.form.controls['document'].value,
      identificationDocumentTypeId:
        this.dataLead && this.dataLead.leadIdentificationDocumentTypeId
          ? this.dataLead.leadIdentificationDocumentTypeId
          : 0,
      names: this.form.controls['leadName'].value,
      firstSurname: this.form.controls['paternalSurname'].value,
      secondSurname: this.form.controls['maternalSurname'].value,
    };

    const orderInformation: OrderInformation = {
      addressName: this.form.controls['address'].value,
    };

    const receptionContactFields: ReceptionContactFields = {
      contactName: this.form.controls['receptionContactName'].value,
      contactPhone: this.form.controls['phoneNumber'].value,
      addressName: this.form.controls['address'].value,
    };

    const fiscalAddressFields: FiscalAddressFields = {
      department: this.form.controls['department'].value,
      province: this.form.controls['province'].value,
      district: this.form.controls['district'].value,
      streetTypeId: this.form.controls['streetType'].value,
      addressName: this.form.controls['fiscalAddressName'].value,
    };

    const validateReceptionFields =
      receptionContactFields.contactName !== '' &&
      receptionContactFields.contactPhone !== '';
    const validateFiscalAddressFields =
      fiscalAddressFields.department !== '' &&
      fiscalAddressFields.province !== '' &&
      fiscalAddressFields.district !== '' &&
      fiscalAddressFields.streetTypeId !== null &&
      fiscalAddressFields.addressName !== '';

    if (!this.receptionContact && validateReceptionFields) {
      serviceEvents.push(
        this.updateLeadContactInformation({
          ...receptionContactFields,
          ...orderInformation,
        }),
      );
    }

    if (this.receptionContact) {
      serviceEvents.push(this.updateLeadContactInformation(orderInformation));
    }

    if (!this.fiscalAddress && validateFiscalAddressFields) {
      serviceEvents.push(
        this.updateLeadInformation({
          ...fiscalAddressFields,
          ...leadInformation,
        }),
      );
    }

    if (this.fiscalAddress) {
      serviceEvents.push(this.updateLeadInformation(leadInformation));
    }

    if (serviceEvents.length === 0) {
      return;
    }

    forkJoin(serviceEvents).subscribe({
      next: () => {
        this.isLoadingSaveEdit = false;
        this.editForm = false;
        this.getCommercialAgreementPreview(this.lead.id);
      },
      error: () => {
        this.editForm = false;
        this.isLoadingSaveEdit = false;
      },
    });
  }

  protected isFormValid(): any {
    const requiredFields: string[] = [
      'hour',
      'leadName',
      'paternalSurname',
      'maternalSurname',
      'document',
      'email',
      'address',
      'total',
    ];
    const receptionContact: string[] = ['receptionContactName', 'phoneNumber'];
    const fiscalAddress: string[] = [
      'department',
      'province',
      'district',
      'streetType',
      'fiscalAddressName',
    ];
    const emailValid = this.form.get('email')?.invalid;

    if (!this.receptionContact) {
      requiredFields.push(...receptionContact);
    }

    if (!this.fiscalAddress) {
      requiredFields.push(...fiscalAddress);
    }

    return this.validateFields(requiredFields) && !emailValid;
  }
}
